import {
  faImage,
  faImageLandscape,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Avatar,
  BackgroundImage,
  Box,
  FileButton,
  Flex,
  Indicator,
  LoadingOverlay,
  Select,
  Text,
} from '@mantine/core'
import { useEffect, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { CollapsibleCard, Icon } from '@flecto/ui'
import { colors } from '@flecto/ui/src/themes/mantine/tokens'
import {
  fileToBase64,
  uploadImageByBase64,
  useCloudflareUrl,
} from '@flecto/utils'

import { FormImages } from '../FormStoreSettings/FormImages'
import { FormSection } from '../FormStoreSettings/FormSection'
import { useFormContext } from '../FormStoreSettings/FormStoreSettings'
import styles from './styles.module.css'

export function CardStoreDesign() {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_store' })

  const form = useFormContext()

  const { getUrl } = useCloudflareUrl()

  const [imgPreviewBg, setImgPreviewBg] = useState<string | null>(
    form.values.bg_image_url ? getUrl(form.values.bg_image_url) : null
  )
  const [imgPreviewLogo, setImgPreviewLogo] = useState<string | null>(
    form.values.logo_image_url ? getUrl(form.values.logo_image_url) : null
  )
  const [isUploadingImageBg, setIsUploadingImageBg] = useState(false)
  const [isUploadingImageLogo, setIsUploadingImageLogo] = useState(false)

  const colorOptions = Object.keys(colors)
    .map((color) => ({ label: color, value: color }))
    .filter((c) => !['brand', 'dark'].includes(c.value))

  const fontsOptions = [
    { label: 'Aeonik Pro', value: '' },
    { label: 'Arial', value: 'arial, sans-serif' },
    { label: 'Roboto', value: 'roboto, sans-serif' },
    { label: 'Helvetica', value: 'helvetica, sans-serif' },
    { label: 'Helvetica Neue', value: 'helvetica neue, sans-serif' },
    { label: 'System UI', value: 'system-ui' },
  ]

  const handleUploadImageBg = async (file: File | null) => {
    if (!file) {
      return
    }

    setIsUploadingImageBg(true)
    const base64 = await fileToBase64(file)
    const img = await uploadImageByBase64(base64)

    form.setFieldValue('bg_image_id', img.id)
    setImgPreviewBg(img.urlTemporary)
    setIsUploadingImageBg(false)
  }

  const handleUploadImageLogo = async (file: File | null) => {
    if (!file) {
      return
    }

    setIsUploadingImageLogo(true)
    const base64 = await fileToBase64(file)
    const img = await uploadImageByBase64(base64)

    form.setFieldValue('logo_image_id', img.id)
    setImgPreviewLogo(img.urlTemporary)
    setIsUploadingImageLogo(false)
  }

  const handleClearImageBg = () => {
    form.setFieldValue('bg_image_id', null)
    form.setFieldValue('bg_image_url', null)
    setImgPreviewBg(null)
  }

  const handleClearImageLogo = () => {
    form.setFieldValue('logo_image_id', null)
    form.setFieldValue('logo_image_url', null)
    setImgPreviewLogo(null)
  }

  useEffect(() => {
    if (form.values.bg_image_url) {
      setImgPreviewBg(getUrl(form.values.bg_image_url))
    }
    if (form.values.logo_image_url) {
      setImgPreviewLogo(getUrl(form.values.logo_image_url))
    }
  }, [form.values])

  return (
    <FormSection>
      <CollapsibleCard opened>
        <CollapsibleCard.Title>
          {t('section_design.title')}
        </CollapsibleCard.Title>
        <CollapsibleCard.Description>
          <Box mr='sm'>{t('section_design.description')}</Box>
        </CollapsibleCard.Description>
        <CollapsibleCard.Content>
          <Flex mt='sm' gap='lg'>
            <Box>
              <Text fw={500} mb='sm'>
                {t('section_design.logo')}
              </Text>
              <Indicator
                disabled={!imgPreviewLogo}
                label={<Icon icon={faTimes} />}
                color='red'
                size={24}
                onClick={handleClearImageLogo}
                style={{ cursor: 'pointer' }}
              >
                <FileButton
                  onChange={handleUploadImageLogo}
                  accept='image/png,image/jpeg'
                >
                  {(props) => (
                    <Avatar h={100} w={100} src={imgPreviewLogo} {...props}>
                      <LoadingOverlay
                        visible={isUploadingImageLogo}
                        loaderProps={{ size: 'sm' }}
                      />
                      <Icon icon={faImage} size='lg' />
                    </Avatar>
                  )}
                </FileButton>
              </Indicator>
            </Box>
            <Box style={{ flex: 1 }}>
              <Text fw={500}>{t('section_design.background_image')}</Text>
              <Indicator
                disabled={!imgPreviewBg}
                label={<Icon icon={faTimes} />}
                color='red'
                size={24}
                onClick={handleClearImageBg}
                style={{ cursor: 'pointer' }}
              >
                <FileButton
                  onChange={handleUploadImageBg}
                  accept='image/png,image/jpeg'
                >
                  {(props) => (
                    <BackgroundImage
                      mt='sm'
                      src={imgPreviewBg ?? ''}
                      className={styles.boxBgImage}
                      {...props}
                    >
                      <LoadingOverlay
                        visible={isUploadingImageBg}
                        loaderProps={{ size: 'sm' }}
                      />
                      {!imgPreviewBg && (
                        <Icon
                          icon={faImageLandscape}
                          size='xl'
                          color='gray.7'
                        />
                      )}
                    </BackgroundImage>
                  )}
                </FileButton>
              </Indicator>
            </Box>
          </Flex>
          <Box mt='xl'>
            <Text fw={500}>{t('section_design.main_color')}</Text>
            <Text c='dimmed' mt='xs' mb='sm' size='sm'>
              {t('section_design.main_color_description')}
            </Text>
            <Flex
              align='center'
              justify='space-between'
              gap='sm'
              rowGap='md'
              wrap='wrap'
              mt='sm'
            >
              {colorOptions.map((color) => (
                <ActionIcon
                  key={color.value}
                  variant='light'
                  className={styles.boxColor}
                  data-selected={color.value === form.values.style_main_color}
                  onClick={() =>
                    form.setFieldValue('style_main_color', color.value)
                  }
                >
                  <Box className={styles.boxColorPreview} bg={color.value} />
                  <Text tt='capitalize'>{color.label}</Text>
                </ActionIcon>
              ))}
            </Flex>
          </Box>
          <Box mt='lg'>
            <Select
              key={form.key('style_font_family')}
              label={t('section_design.font_family')}
              data={fontsOptions}
              defaultValue={form.values.style_font_family}
              allowDeselect={false}
              {...form.getInputProps('style_font_family')}
            />
          </Box>
        </CollapsibleCard.Content>
      </CollapsibleCard>
      <FormImages images={[require('./image.jpeg')]} />
    </FormSection>
  )
}
